import {
  errorsOnly,
  shouldEscalate,
  validateReceipt,
  type ValidationIssue,
} from '../receipt/validate.js';
import { absorbTipLines } from '../receipt/tips.js';
import type { Person, Receipt } from '../types.js';
import { resolveOps, type ResolveResult } from '../split/resolve.js';
import { validateOps, type SplitContext, type SplitOp } from '../split/dsl.js';
import { applyReply, type ApplyOptions, type SplitReply } from '../split/reply.js';
import type { SplitHistory } from './prompts.js';
import { InferenceError, type InferenceProvider, type ReceiptInput } from './provider.js';

/**
 * The escalation ladder from the plan, minus the cloud rung.
 *
 * Deliberately stops short of calling a cloud provider itself: sending a
 * receipt off the device is the user's decision, so this returns a verdict and
 * the UI owns the consent step. Core never makes a network call on its own.
 */

export type StructureOutcome =
  /** Parsed and the arithmetic reconciles. Warnings may still be present. */
  | { status: 'ok'; receipt: Receipt; issues: ValidationIssue[]; attempts: number }
  /** Parsed, but the numbers don't add up — repair UI or cloud retry. */
  | { status: 'needs-repair'; receipt: Receipt; issues: ValidationIssue[]; attempts: number }
  /** Nothing usable came back at all. */
  | { status: 'failed'; errors: string[]; attempts: number };

export interface StructureOptions {
  /** Extra on-device attempts after the first. */
  retries?: number;
  signal?: AbortSignal;
}

export async function structureReceipt(
  provider: InferenceProvider,
  input: ReceiptInput,
  options: StructureOptions = {},
): Promise<StructureOutcome> {
  const { retries = 1, signal } = options;

  let attempts = 0;
  let lastErrors: string[] = ['no attempt was made'];
  let lastReceipt: Receipt | null = null;
  let lastIssues: ValidationIssue[] = [];

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    attempts += 1;
    try {
      // Whatever the model called the gratuity line, it is a tip, not a dish.
      const receipt = absorbTipLines(await provider.structureReceipt(input, signal));
      const issues = validateReceipt(receipt);
      if (!shouldEscalate(issues)) {
        return { status: 'ok', receipt, issues, attempts };
      }
      lastReceipt = receipt;
      lastIssues = issues;
      lastErrors = errorsOnly(issues).map((issue) => issue.message);
    } catch (error) {
      lastReceipt = null;
      lastErrors =
        error instanceof InferenceError && error.details.length > 0
          ? error.details
          : [error instanceof Error ? error.message : String(error)];
    }
  }

  if (lastReceipt) {
    return { status: 'needs-repair', receipt: lastReceipt, issues: lastIssues, attempts };
  }
  return { status: 'failed', errors: lastErrors, attempts };
}

export type SplitOutcome =
  | {
      status: 'ok';
      /** The raw reply, kept so the UI can re-apply it with a head count later. */
      reply: SplitReply;
      /** Roster after the sentence: known people, introduced ones, guests. */
      people: Person[];
      ops: SplitOp[];
      resolved: ResolveResult;
      askHeadCount: boolean;
      /** The speaker named themselves in the sentence ("Tudor (me)"). */
      speakerAlias?: string;
    }
  | { status: 'failed'; errors: string[] };

export interface SplitOptions extends ApplyOptions {
  signal?: AbortSignal;
  /** Earlier messages and the current assignment, on a follow-up turn. */
  history?: SplitHistory;
}

/**
 * Parse a sentence into operations, rejecting anything that references an
 * entity we never handed the model. A hallucinated name fails here, loudly,
 * rather than quietly redistributing somebody's money.
 */
export async function parseSplit(
  provider: InferenceProvider,
  context: SplitContext,
  utterance: string,
  options: SplitOptions = {},
): Promise<SplitOutcome> {
  let reply: SplitReply;
  try {
    reply = await provider.parseSplit(context, utterance, options.signal, options.history);
  } catch (error) {
    return {
      status: 'failed',
      errors:
        error instanceof InferenceError && error.details.length > 0
          ? error.details
          : [error instanceof Error ? error.message : String(error)],
    };
  }

  return applySplitReply(reply, context, utterance, options);
}

/**
 * The model-free half of parseSplit, split out so the UI can re-run it when
 * the user answers "how many were you?" without another inference call.
 */
export function applySplitReply(
  reply: SplitReply,
  context: SplitContext,
  utterance: string,
  options: ApplyOptions = {},
): SplitOutcome {
  const applied = applyReply(reply, context, utterance, options);
  if (applied.errors.length > 0) return { status: 'failed', errors: applied.errors };

  const finalContext: SplitContext = { ...context, people: applied.people };
  const opErrors = validateOps(applied.ops, finalContext);
  if (opErrors.length > 0) {
    return {
      status: 'failed',
      errors: opErrors.map((e) => `op ${e.opIndex}: ${e.message}`),
    };
  }

  return {
    status: 'ok',
    reply,
    people: applied.people,
    ops: applied.ops,
    resolved: resolveOps(applied.ops, finalContext),
    askHeadCount: applied.askHeadCount,
    ...(applied.speakerAlias ? { speakerAlias: applied.speakerAlias } : {}),
  };
}
